import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-day-picker/dist/style.css';
import './DateRangePicker.css';

/**
 * A fluid date range picker that allows:
 * - Single date selection (click once, then click away)
 * - Range selection (click start, then end)
 * - Visual feedback during selection
 * - Dropdown calendar interface
 */
export const DateRangePicker = ({
  startDate,
  endDate,
  onDateChange,
  maxDate,
  minDate,
  placeholder = 'Select date range',
  openKey = 0
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState({
    from: undefined,
    to: undefined
  });
  const [savedRange, setSavedRange] = useState({
    from: startDate ? parseDateString(startDate) : undefined,
    to: endDate ? parseDateString(endDate) : undefined
  });
  const [displayedMonth, setDisplayedMonth] = useState(new Date());
  // 'days' = classic day calendar; 'months' = month grid for quickly pulling
  // multi-month spans (e.g. Jan-Jun) without day-by-day navigation
  const [pickerMode, setPickerMode] = useState('days');
  const [monthRange, setMonthRange] = useState({ from: undefined, to: undefined }); // first-of-month Dates
  const [displayedYear, setDisplayedYear] = useState(new Date().getFullYear());
  const dropdownRef = useRef(null);
  const popupRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState(null);

  // Parse date string in YYYY-MM-DD format without timezone issues
  function parseDateString(dateStr) {
    if (!dateStr) return undefined;
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
    return new Date(year, month - 1, day); // month is 0-indexed
  }

  // Update saved range when props change
  useEffect(() => {
    setSavedRange({
      from: startDate ? parseDateString(startDate) : undefined,
      to: endDate ? parseDateString(endDate) : undefined
    });
  }, [startDate, endDate]);

  // Imperative open (e.g. the report filter bar opens the calendar the
  // moment the user clicks the "Custom" segment — no second click needed)
  useEffect(() => {
    if (openKey > 0) {
      setRange({ from: undefined, to: undefined });
      setMonthRange({ from: undefined, to: undefined });
      setDisplayedMonth((prev) => prev || new Date());
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  // Clear selection and start fresh when opening calendar
  const handleOpen = () => {
    setRange({ from: undefined, to: undefined });
    setMonthRange({ from: undefined, to: undefined });
    setDisplayedMonth(savedRange?.from || new Date());
    setDisplayedYear((savedRange?.from || new Date()).getFullYear());
    setIsOpen(true);
  };

  const formatYmd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Commit a selection to the parent and close the calendar
  const commitRange = (from, to) => {
    onDateChange({ start: formatYmd(from), end: formatYmd(to) });
    setSavedRange({ from, to });
    setIsOpen(false);
    setRange({ from: undefined, to: undefined });
    setMonthRange({ from: undefined, to: undefined });
  };

  // Commit a month-mode selection: whole calendar months, clamped to min/max
  // (an end month capped by maxDate becomes month-to-date, like the presets)
  const commitMonthRange = (mFrom, mTo) => {
    let from = new Date(mFrom.getFullYear(), mFrom.getMonth(), 1);
    let to = new Date(mTo.getFullYear(), mTo.getMonth() + 1, 0);
    const min = parseMinDate();
    const max = parseMaxDate();
    if (min && from < min) from = min;
    if (max && to > max) to = max;
    if (from > to) return;
    commitRange(from, to);
  };

  const monthKey = (d) => d.getFullYear() * 12 + d.getMonth();

  const handleMonthClick = (monthDate) => {
    if (!monthRange.from || monthRange.to) {
      setMonthRange({ from: monthDate, to: undefined });
      return;
    }
    // Either click order works; normalize to earliest-first
    const [from, to] = monthDate < monthRange.from
      ? [monthDate, monthRange.from]
      : [monthRange.from, monthDate];
    setMonthRange({ from, to });
    // Mirror day mode: completing the range commits after a brief beat
    setTimeout(() => commitMonthRange(from, to), 300);
  };

  // Select an entire calendar month in one click, clamped to min/max
  // (e.g. "This Month" with maxDate=today becomes month-to-date)
  const selectFullMonth = (monthDate) => {
    let from = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    let to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const min = parseMinDate();
    const max = parseMaxDate();
    if (min && from < min) from = min;
    if (max && to > max) to = max;
    if (from > to) return;

    onDateChange({ start: formatYmd(from), end: formatYmd(to) });
    setSavedRange({ from, to });
    setIsOpen(false);
    setRange({ from: undefined, to: undefined });
  };

  const sameMonth = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

  // The dropdown renders in a portal with fixed positioning so it can't be
  // clipped by overflow:auto/hidden ancestors (e.g. the main layout scroll
  // area) or trapped under sibling stacking contexts.
  const updatePopupPosition = useCallback(() => {
    const anchor = dropdownRef.current;
    const popup = popupRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const popupHeight = popup ? popup.offsetHeight : 0;
    const popupWidth = popup ? popup.offsetWidth : 0;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openUp = popupHeight > 0 && spaceBelow < popupHeight && rect.top - 8 >= popupHeight;
    // Clamp into the viewport: if the popup fits neither below nor above the
    // anchor, pin it to the viewport edge (the dropdown's max-height +
    // overflow-y take over from there)
    const top = openUp ? rect.top - 8 - popupHeight : rect.bottom + 8;
    setPopupStyle({
      position: 'fixed',
      top: Math.max(8, Math.min(top, window.innerHeight - popupHeight - 8)),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - popupWidth - 8)),
      zIndex: 9999
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePopupPosition();
    window.addEventListener('resize', updatePopupPosition);
    window.addEventListener('scroll', updatePopupPosition, true);
    return () => {
      window.removeEventListener('resize', updatePopupPosition);
      window.removeEventListener('scroll', updatePopupPosition, true);
    };
  }, [isOpen, updatePopupPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {

      const insideInput = dropdownRef.current && dropdownRef.current.contains(event.target);
      const insidePopup = popupRef.current && popupRef.current.contains(event.target);
      if (!insideInput && !insidePopup) {
        // Clicking away commits the pending selection (same as Apply);
        // a lone start date/month becomes a single-day/month range
        if (pickerMode === 'months' && monthRange?.from) {
          commitMonthRange(monthRange.from, monthRange.to || monthRange.from);
        } else if (pickerMode === 'days' && range?.from) {
          commitRange(range.from, range.to || range.from);
        } else {
          // No selection made - keep the previous saved range
          setIsOpen(false);
          setRange({ from: undefined, to: undefined });
          setMonthRange({ from: undefined, to: undefined });
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {

      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, range, monthRange, pickerMode, onDateChange]);

  const handleSelect = (selectedRange) => {

    // If user already has a 'from' date and selects another date, it's a range
    if (range?.from && selectedRange?.from && selectedRange?.to) {
      onDateChange({
        start: formatYmd(selectedRange.from),
        end: formatYmd(selectedRange.to)
      });

      setSavedRange(selectedRange);

      // Close after a brief delay to show the selection
      setTimeout(() => {

        setIsOpen(false);
        setRange({ from: undefined, to: undefined });
      }, 300);
    } else {
      // Just set the range, don't close - wait for user to either select second date or click away

      setRange(selectedRange);
    }
  };

  const formatDisplayDate = () => {
    if (!savedRange?.from) {
      return placeholder;
    }

    const formatDate = (date) => {
      return format(date, 'MMM d, yyyy');
    };

    if (!savedRange.to || savedRange.from.getTime() === savedRange.to.getTime()) {
      return formatDate(savedRange.from);
    }

    return `${formatDate(savedRange.from)} - ${formatDate(savedRange.to)}`;
  };

  const parseMaxDate = () => {
    if (!maxDate) return undefined;
    return parseDateString(maxDate);
  };

  const parseMinDate = () => {
    if (!minDate) return undefined;
    return parseDateString(minDate);
  };

  return (
    <div className="date-range-picker" ref={dropdownRef}>
      <div
        className={`date-range-picker-input ${isOpen ? 'open' : ''}`}
        onClick={() => isOpen ? setIsOpen(false) : handleOpen()}
      >
        <Calendar size={16} style={{ color: 'var(--lodge-onyx-500)', flexShrink: 0 }} />
        <span className={savedRange?.from ? 'drp-has-value' : 'drp-placeholder'}>
          {formatDisplayDate()}
        </span>
        <ChevronDown className="dropdown-arrow" size={13} aria-hidden="true" />
      </div>

      {isOpen && createPortal(
        <div
          className="date-range-picker-dropdown"
          ref={popupRef}
          style={popupStyle || { position: 'fixed', visibility: 'hidden' }}
        >
          <div className="date-range-picker-header">
            <div className="drp-mode-toggle" role="tablist" aria-label="Selection mode">
              <button
                type="button"
                role="tab"
                aria-selected={pickerMode === 'days'}
                className={'drp-mode-btn' + (pickerMode === 'days' ? ' active' : '')}
                onClick={() => setPickerMode('days')}
              >
                Days
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={pickerMode === 'months'}
                className={'drp-mode-btn' + (pickerMode === 'months' ? ' active' : '')}
                onClick={() => setPickerMode('months')}
              >
                Months
              </button>
            </div>
            {/* Always rendered (visibility-toggled) so the popup doesn't grow
                and shift when the first date is picked */}
            <button
              className="date-range-picker-clear"
              style={{ visibility: (range?.from || monthRange?.from) ? 'visible' : 'hidden' }}
              onClick={(e) => {
                e.stopPropagation();
                setRange({ from: undefined, to: undefined });
                setMonthRange({ from: undefined, to: undefined });
              }}
            >
              Clear
            </button>
          </div>

          {(() => {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const showDisplayed = !sameMonth(displayedMonth, today) && !sameMonth(displayedMonth, lastMonth);
            const min = parseMinDate();
            const max = parseMaxDate();
            const todayInBounds = (!min || today >= min) && (!max || today <= max);
            return (
              <div className="date-range-picker-presets">
                {todayInBounds && (
                  <button className="date-range-picker-preset" onClick={() => commitRange(today, today)}>
                    Today
                  </button>
                )}
                <button className="date-range-picker-preset" onClick={() => selectFullMonth(today)}>
                  This Month
                </button>
                <button className="date-range-picker-preset" onClick={() => selectFullMonth(lastMonth)}>
                  Last Month
                </button>
                {showDisplayed && (
                  <button className="date-range-picker-preset" onClick={() => selectFullMonth(displayedMonth)}>
                    All of {format(displayedMonth, 'MMM yyyy')}
                  </button>
                )}
              </div>
            );
          })()}

          <div className="drp-cal-area">
            {pickerMode === 'days' ? (
              <DayPicker
                mode="range"
                selected={range}
                onSelect={handleSelect}
                month={displayedMonth}
                onMonthChange={setDisplayedMonth}
                disabled={{ after: parseMaxDate(), before: parseMinDate() }}
                numberOfMonths={1}
                showOutsideDays
                className="date-range-picker-calendar"
              />
            ) : (
              <div className="drp-months-view">
                <div className="drp-months-caption">
                  <span className="drp-months-year">{displayedYear}</span>
                  <div className="drp-months-nav">
                    <button
                      type="button"
                      className="drp-mnav-btn"
                      aria-label="Previous year"
                      onClick={() => setDisplayedYear((y) => y - 1)}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      className="drp-mnav-btn"
                      aria-label="Next year"
                      onClick={() => setDisplayedYear((y) => y + 1)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
                {/* Rows of three are calendar quarters; the rail makes that legible */}
                <div className="drp-months-grid">
                  {Array.from({ length: 4 }, (_, q) => (
                    <React.Fragment key={q}>
                      <span className="drp-quarter-label" aria-hidden="true">Q{q + 1}</span>
                      {Array.from({ length: 3 }, (_, c) => {
                        const i = q * 3 + c;
                        const monthDate = new Date(displayedYear, i, 1);
                        const monthEnd = new Date(displayedYear, i + 1, 0);
                        const min = parseMinDate();
                        const max = parseMaxDate();
                        const isDisabled = (max && monthDate > max) || (min && monthEnd < min);
                        const k = monthKey(monthDate);
                        const fromK = monthRange.from ? monthKey(monthRange.from) : null;
                        const toK = monthRange.to ? monthKey(monthRange.to) : null;
                        const isEdge = fromK !== null && (k === fromK || (toK !== null && k === toK));
                        const inRange = fromK !== null && toK !== null && k > fromK && k < toK;
                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={isDisabled}
                            className={
                              'drp-month-cell' +
                              (isEdge ? ' selected' : '') +
                              (inRange ? ' in-range' : '')
                            }
                            onClick={() => handleMonthClick(monthDate)}
                          >
                            {format(monthDate, 'MMM')}
                          </button>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="date-range-picker-footer">
            <div className="date-range-picker-hint">
              {pickerMode === 'days' ? (
                <>
                  {!range?.from && 'Select a start date'}
                  {range?.from && (!range?.to || range.from.getTime() === range.to.getTime()) && (
                    <>
                      <span className="drp-hint-date">{format(range.from, 'MMM d, yyyy')}</span>
                      {' — add end date or Apply for one day'}
                    </>
                  )}
                  {range?.from && range?.to && range.from.getTime() !== range.to.getTime() && (
                    <span className="drp-hint-date">
                      {format(range.from, 'MMM d')} – {format(range.to, 'MMM d, yyyy')}
                    </span>
                  )}
                </>
              ) : (
                <>
                  {!monthRange?.from && 'Select a start month'}
                  {monthRange?.from && !monthRange?.to && (
                    <>
                      <span className="drp-hint-date">{format(monthRange.from, 'MMM yyyy')}</span>
                      {' — add end month or Apply for one month'}
                    </>
                  )}
                  {monthRange?.from && monthRange?.to && (
                    <span className="drp-hint-date">
                      {format(
                        monthRange.from,
                        monthRange.from.getFullYear() === monthRange.to.getFullYear() ? 'MMM' : 'MMM yyyy'
                      )} – {format(monthRange.to, 'MMM yyyy')}
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="date-range-picker-actions">
              <button
                className="drp-btn drp-btn-cancel"
                onClick={() => {
                  setIsOpen(false);
                  setRange({ from: undefined, to: undefined });
                  setMonthRange({ from: undefined, to: undefined });
                }}
              >
                Cancel
              </button>
              <button
                className="drp-btn drp-btn-apply"
                disabled={pickerMode === 'months' ? !monthRange?.from : !range?.from}
                onClick={() =>
                  pickerMode === 'months'
                    ? commitMonthRange(monthRange.from, monthRange.to || monthRange.from)
                    : commitRange(range.from, range.to || range.from)
                }
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
